import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { SelectField } from 'components';
import validate from 'validate.js';
import { Grid, TextField } from '@mui/material';
import { SetFormState } from '../../../actions/IncidentTrackerActions';

const schema = {
	technology: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	layer: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {},
	container: {
		marginTop: theme.spacing(3)
	},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	noBorder: {
		border: 'none',
	}
}));

const IncidentConfigurationLayerForm = (props) => {
	const classes = useStyles();
	const {
		onChange,
		disabled,
		technologies,
		defaultConfig = undefined,
		...rest
	} = props;
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.incidentTrackerReducer.formState);

	const updateFormState = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _getTechnologyId = (technologyName) => {
		const technology = technologies.find(tech => tech.text === technologyName);
		return technology?.value;
	};

	const [selectedTechnology, setSelectedTechnology] = useState(_getTechnologyId(defaultConfig?.technology));
	const [layer, setLayer] = useState(defaultConfig?.layer ?? '');
	const id = defaultConfig?.id;

	const _handleTechnologyChange = (event) => {
		setSelectedTechnology(event.target.value);
		updateFormState(event);
		onChange({ id, name: 'layers', dependencyId: event.target.value, value: layer });
	};

	const _handleLayerChange = (event) => {
		setLayer(event.target.value);
		updateFormState(event);
		onChange({ id, name: 'layers', dependencyId: selectedTechnology, value: event.target.value });
	};

	useEffect(() => {
		const errors = validate(formState.values, schema);
		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	useEffect(() => {
		setFormState({
			...formState,
			values: {
				technology: selectedTechnology,
				layer
			}
		});
	}, []);

	const hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	return (
		<Grid className={classes.container} spacing="8" container md={12} xs={12}>
			<Grid item md={6} xs={6}>
				<SelectField
					id="technology"
					name="technology"
					disabled={disabled}
					hintText="Technology"
					menuItems={technologies || []}
					value={selectedTechnology || ''}
					onChange={_handleTechnologyChange}
					error={hasError('technology') ? formState.errors.technology[0] : null}
				/>
			</Grid>
			<Grid item md={6} xs={6}>
				<TextField
					id="layer"
					name="layer"
					label="Layer"
					hintText="Add a value"
					variant="outlined"
					fullWidth
					disabled={!selectedTechnology || disabled}
					value={layer || ''}
					onChange={_handleLayerChange}
					error={hasError('layer')}
					helperText={
						hasError('layer') ? formState.errors.layer[0] : null
					}
				/>
			</Grid>
		</Grid>
	);
};

IncidentConfigurationLayerForm.propTypes = {
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	technologies: PropTypes.array,
	defaultConfig: PropTypes.object
};

export default IncidentConfigurationLayerForm;
